import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { auth, db } from '../../../servicos/firebase';
import './PainelMaster.css';

// Componentes Extraídos
import {
    IconLock,
    IconLogout,
    IconMessage,
    IconPlus
} from '../componentes/Icons';
import EstatisticasMaster from './secoes/EstatisticasMaster';
import GradeUnidades from './secoes/GradeUnidades';
import ModalAdicionarUnidade from './secoes/ModalAdicionarUnidade';
import ModalComunicado from './secoes/ModalComunicado';
import ModalEditarUnidade from './secoes/ModalEditarUnidade';
import ModalUsuarios from './secoes/ModalUsuarios';
import PendingGrid from './secoes/PendingGrid';

const SUBSCRIPTION_PLANS = {
    profissional: { name: 'Profissional', price: 120.00, limit: 20 }
};

const BILLING_CYCLES = [
    { months: 1, name: 'Mensal', discount: 0 },
    { months: 3, name: 'Trimestral', discount: 5 },
    { months: 6, name: 'Semestral', discount: 10 },
    { months: 12, name: 'Anual', discount: 20 }
];

const PainelMaster = ({ onBack }) => {
    const mastersList = ['claudio56908@gmail.com'];
    const userEmail = auth.currentUser?.email?.toLowerCase();
    const isAuthorized = mastersList.includes(userEmail);

    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [saving, setSaving] = useState(false);
    const [userCounts, setUserCounts] = useState({});
    const [tenantUsers, setTenantUsers] = useState({});
    const [viewingUsers, setViewingUsers] = useState(null);
    const [isOnline, setIsOnline] = useState(true);
    const [isBroadcasting, setIsBroadcasting] = useState(false);
    const [broadcastData, setBroadcastData] = useState({ active: false, message: '', type: 'info' });
    const [customAlert, setCustomAlert] = useState({ show: false, message: '', type: 'info' });
    const [activeMasterTab, setActiveMasterTab] = useState('tenants');
    const [pendingRegistrations, setPendingRegistrations] = useState([]);

    // Form States
    const [newId, setNewId] = useState('');
    const [newName, setNewName] = useState('');
    const [newPlan, setNewPlan] = useState('profissional');
    const [newCycle, setNewCycle] = useState(1);
    const [newOwnerPhone, setNewOwnerPhone] = useState('');
    const [newAdmins, setNewAdmins] = useState('');
    const [editAdmins, setEditAdmins] = useState('');
    const [editPlan, setEditPlan] = useState('profissional');
    const [editOwnerPhone, setEditOwnerPhone] = useState('');
    const [editDueDate, setEditDueDate] = useState('');
    const [editCycle, setEditCycle] = useState(1);

    const handleEditCycleChange = (value) => {
        const months = Number(value);
        setEditCycle(months);

        const now = new Date();
        const dueDate = new Date();
        dueDate.setMonth(now.getMonth() + months);
        setEditDueDate(dueDate.toISOString().split('T')[0]);
    };

    const handleEditPlanChange = (value) => {
        setEditPlan(value);
        const now = new Date();
        const dueDate = new Date();
        dueDate.setMonth(now.getMonth() + Number(editCycle));
        setEditDueDate(dueDate.toISOString().split('T')[0]);
    };

    const showAlert = (message, type = 'info') => {
        setCustomAlert({ show: true, message, type });
        if (type === 'info' || type === 'success') {
            setTimeout(() => setCustomAlert(prev => ({ ...prev, show: false })), 4000);
        }
    };

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "tenants"),
            (snapshot) => {
                const tenantList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setTenants(tenantList);
                setLoading(false);
                setIsOnline(true);
                tenantList.forEach(tenant => fetchUserCount(tenant.id));
            },
            (error) => {
                console.error("❌ Firebase Error:", error);
                setLoading(false);
                setIsOnline(false);
            }
        );

        const unsubPending = onSnapshot(collection(db, "pending_registrations"),
            (snapshot) => {
                const pendingList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setPendingRegistrations(pendingList);
            }
        );

        const unsubBroadcast = onSnapshot(doc(db, "global_config", "broadcast"), (snap) => {
            if (snap.exists()) setBroadcastData(snap.data());
        });

        const timer = setTimeout(() => setLoading(false), 5000);

        return () => {
            unsub();
            unsubPending();
            unsubBroadcast();
            clearTimeout(timer);
        };
    }, []);

    const fetchUserCount = async (tenantId) => {
        try {
            const usersRef = collection(db, "users");
            const q = query(usersRef, where("barbershopId", "==", tenantId));
            const snap = await getDocs(q);
            setUserCounts(prev => ({ ...prev, [tenantId]: snap.size }));
            setTenantUsers(prev => ({
                ...prev,
                [tenantId]: snap.docs.map(d => d.data())
            }));
        } catch (error) {
            console.error(`Erro ao buscar usuários de ${tenantId}:`, error);
        }
    };

    const handleAddTenant = async () => {
        if (!newId || !newName) return showAlert('ID e Nome são obrigatórios', 'danger');
        setSaving(true);
        try {
            const tenantRef = doc(db, "tenants", newId);
            const snap = await getDoc(tenantRef);
            if (snap.exists()) throw new Error('Este ID já está em uso.');

            const adminArr = newAdmins.split(',').map(e => e.trim().toLowerCase()).filter(e => e);

            const now = new Date();
            const dueDate = new Date();
            dueDate.setMonth(now.getMonth() + Number(newCycle));

            await setDoc(tenantRef, {
                name: newName,
                ownerPhone: newOwnerPhone,
                adminEmails: adminArr,
                status: 'active',
                plan: newPlan,
                billingCycle: Number(newCycle),
                dueDate: dueDate.toISOString(),
                createdAt: now.toISOString()
            });

            showAlert('Unidade cadastrada com sucesso!', 'success');
            setIsAdding(false);
            setNewId(''); setNewName(''); setNewAdmins('');
            setNewCycle(1);
        } catch (e) {
            showAlert(e.message, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (tenant) => {
        try {
            const newStatus = tenant.status === 'active' ? 'inactive' : 'active';
            await updateDoc(doc(db, "tenants", tenant.id), { status: newStatus });
            showAlert(`Unidade ${newStatus === 'active' ? 'reativada' : 'suspensa'}.`, 'info');
        } catch (e) {
            showAlert('Erro ao alterar status.', 'danger');
        }
    };

    const handleStartEdit = (tenant) => {
        setEditingTenant(tenant);
        setEditAdmins(tenant.adminEmails?.join(', ') || '');
        setEditPlan(tenant.plan || 'profissional');
        setEditCycle(tenant.billingCycle || 1);
        setEditOwnerPhone(tenant.ownerPhone || '');
        setEditDueDate(tenant.dueDate || '');
        setIsEditing(true);
    };

    const handleCopyLink = (tenantId) => {
        const link = `${window.location.origin}/?shop=${tenantId}`;
        navigator.clipboard.writeText(link);
        showAlert('Link copiado para a área de transferência!', 'success');
    };

    const handleContactOwner = (phone) => {
        if (!phone) return showAlert('Telefone não cadastrado.', 'warning');
        const cleanPhone = phone.replace(/\D/g, '');
        window.open(`https://wa.me/55${cleanPhone}`, '_blank');
    };

    const handleSaveTenantDetails = async () => {
        setSaving(true);
        try {
            const adminArr = editAdmins.split(',').map(e => e.trim().toLowerCase()).filter(e => e);
            await updateDoc(doc(db, "tenants", editingTenant.id), {
                adminEmails: adminArr,
                plan: editPlan,
                billingCycle: Number(editCycle),
                ownerPhone: editOwnerPhone,
                dueDate: editDueDate
            });
            showAlert('Configurações salvas!', 'success');
            setIsEditing(false);
        } catch (e) {
            showAlert('Erro ao salvar.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateBroadcast = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, "global_config", "broadcast"), broadcastData);
            showAlert('Broadcast atualizado!', 'success');
            setIsBroadcasting(false);
        } catch (e) {
            showAlert('Erro ao atualizar broadcast.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleClearBroadcast = async () => {
        setSaving(true);
        try {
            const reset = { ...broadcastData, active: false };
            await setDoc(doc(db, "global_config", "broadcast"), reset);
            setBroadcastData(reset);
            showAlert('Broadcast desativado.', 'info');
            setIsBroadcasting(false);
        } catch (e) {
            showAlert('Erro ao limpar broadcast.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async (user) => {
        if (window.confirm(`Excluir ${user.name} permanentemente?`)) {
            try {
                await deleteDoc(doc(db, "users", user.uid));
                showAlert('Usuário excluído.', 'info');
                // Refresh list
                const updatedUsers = viewingUsers.users.filter(u => u.uid !== user.uid);
                setViewingUsers({ ...viewingUsers, users: updatedUsers });
                fetchUserCount(viewingUsers.tenantId);
            } catch (e) {
                showAlert('Erro ao excluir usuário.', 'danger');
            }
        }
    };

    const handleDeleteTenant = async (tenantId, tenantName) => {
        if (window.confirm(`⚠️ ATENÇÃO: Excluir a unidade "${tenantName}" permanentemente? Todos os dados serão perdidos.`)) {
            if (window.confirm(`Confirmação final: Digite o ID "${tenantId}" para apagar:`)) {
                try {
                    await deleteDoc(doc(db, "tenants", tenantId));
                    showAlert('Unidade excluída com sucesso.', 'success');
                } catch (e) {
                    showAlert('Erro ao excluir unidade.', 'danger');
                }
            }
        }
    };

    const handleActivateRegistration = async (reg) => {
        if (!reg.id) return showAlert('ID da solicitação é obrigatório', 'danger');
        setSaving(true);
        try {
            // Robust slug/ID handling
            const targetSlug = reg.shopSlug || reg.id.replace(/\s+/g, '-').toLowerCase();
            const tenantRef = doc(db, "tenants", targetSlug);
            const tenantExists = await getDoc(tenantRef);

            const now = new Date();
            const dueDate = new Date();
            // Ensure billingCycle is a number and fallback to 1
            const cycleMonths = Number(reg.billingCycle) || 1;
            dueDate.setMonth(now.getMonth() + cycleMonths);

            const tenantData = {
                name: reg.shopName || 'Nova Unidade',
                ownerPhone: reg.ownerPhone || reg.phone || reg.whatsapp || '',
                adminEmails: [reg.ownerEmail.toLowerCase()],
                status: 'active',
                plan: reg.plan || 'profissional',
                billingCycle: cycleMonths,
                dueDate: dueDate.toISOString(),
                createdAt: tenantExists.exists() ? (tenantExists.data().createdAt || now.toISOString()) : now.toISOString(),
                ownerName: reg.ownerName || '',
                trialMode: false
            };

            // Create or update tenant (trial to active)
            await setDoc(tenantRef, tenantData, { merge: true });

            // Remove pending registration
            await deleteDoc(doc(db, "pending_registrations", reg.id));

            showAlert('Loja ativada e criada com sucesso!', 'success');
            setActiveMasterTab('tenants');
        } catch (e) {
            console.error("Erro ao ativar loja:", e);
            showAlert(`Erro: ${e.message}`, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRegistration = async (id) => {
        if (window.confirm('Excluir esta solicitação permanentemente?')) {
            try {
                await deleteDoc(doc(db, "pending_registrations", id));
                showAlert('Solicitação excluída.', 'info');
            } catch (error) {
                console.error("Erro ao excluir solicitação:", error);
                showAlert('Erro ao excluir solicitação.', 'danger');
            }
        }
    };

    if (!isAuthorized) {
        return (
            <div className="master-admin-panel" style={{ height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px', background: 'rgba(255,255,255,0.02)', borderRadius: '30px', border: '1px solid rgba(255,77,77,0.2)' }}>
                    <div style={{ color: '#ff4d4d', marginBottom: '20px' }}>
                        <IconLock />
                    </div>
                    <h1 style={{ color: '#ff4d4d', marginTop: '20px', fontWeight: '800' }}>ACESSO RESTRITO</h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)', margin: '20px 0 40px 0', fontSize: '1.1rem' }}>
                        Esta área é exclusiva para administradores globais da plataforma StartWebb.
                    </p>
                    <button className="create-tenant-btn" style={{ margin: '0 auto' }} onClick={onBack}>Voltar à Segurança</button>
                </div>
            </div>
        );
    }

    if (loading) return (
        <div className="master-loading">
            <div className="loading-spinner"></div>
            <p style={{ letterSpacing: '2px', fontWeight: '600' }}>STARTWEBB MANAGER</p>
        </div>
    );

    const activeTenantsCount = tenants.filter(t => t.status === 'active').length;
    const estimatedRevenue = tenants
        .filter(t => t.status === 'active')
        .reduce((sum, t) => {
            const planBase = SUBSCRIPTION_PLANS[t.plan || 'profissional']?.price || 0;
            const cycle = BILLING_CYCLES.find(c => c.months === (t.billingCycle || 1));
            const monthlyPrice = (planBase * (1 - (cycle?.discount || 0) / 100));
            return sum + monthlyPrice;
        }, 0);

    return (
        <div className="master-admin-panel">
            {customAlert.show && (
                <div className={`m-alert-overlay ${customAlert.type} fade-in`}>
                    <div className="m-alert-content">
                        <span className="m-alert-icon">
                            {customAlert.type === 'danger' ? '✕' :
                                customAlert.type === 'success' ? '✓' : '⚠'}
                        </span>
                        <p>{customAlert.message}</p>
                        <button className="m-close-alert" onClick={() => setCustomAlert({ ...customAlert, show: false })}>&times;</button>
                    </div>
                </div>
            )}

            <header className="master-header">
                <div className="master-title-area">
                    <h1>StartWebb <span>Manager</span></h1>
                    <p>
                        Dashboard Master
                        <span className={isOnline ? "m-status online" : "m-status offline"}>
                            {isOnline ? "Sistema Operacional" : "Erro de Conexão"}
                        </span>
                    </p>
                </div>
                <button className="master-back-btn" onClick={onBack}>
                    <IconLogout />
                    Voltar ao Site
                </button>
            </header>

            <main className="master-content">
                <EstatisticasMaster
                    tenantsCount={tenants.length}
                    estimatedRevenue={estimatedRevenue}
                    activeTenantsCount={activeTenantsCount}
                />

                <div className="master-actions-bar">
                    <h2 className="section-title">Gestão de Unidades</h2>
                    <div className="actions-group" style={{ display: 'flex', gap: '15px' }}>
                        <button
                            className="create-tenant-btn global-msg"
                            style={{ background: 'rgba(218, 165, 32, 0.05)', border: '1px solid var(--gold-primary)', color: 'var(--gold-primary)' }}
                            onClick={() => setIsBroadcasting(true)}
                        >
                            <IconMessage />
                            <span>Mensagem Global</span>
                        </button>
                        <button className="create-tenant-btn" onClick={() => setIsAdding(true)}>
                            <IconPlus />
                            <span>Nova Unidade</span>
                        </button>
                    </div>
                </div>

                <div className="master-nav-tabs">
                    <button
                        className={`nav-tab ${activeMasterTab === 'tenants' ? 'active' : ''}`}
                        onClick={() => setActiveMasterTab('tenants')}
                    >
                        UNIDADES ATIVAS ({tenants.length})
                    </button>
                    <button
                        className={`nav-tab ${activeMasterTab === 'pending' ? 'active' : ''}`}
                        onClick={() => setActiveMasterTab('pending')}
                    >
                        NOVAS SOLICITAÇÕES ({pendingRegistrations.length})
                        {pendingRegistrations.length > 0 && <span className="m-new-badge">NOVO</span>}
                    </button>
                </div>

                {activeMasterTab === 'tenants' ? (
                    <GradeUnidades
                        tenants={tenants}
                        SUBSCRIPTION_PLANS={SUBSCRIPTION_PLANS}
                        userCounts={userCounts}
                        tenantUsers={tenantUsers}
                        handleCopyLink={handleCopyLink}
                        handleContactOwner={handleContactOwner}
                        handleStartEdit={handleStartEdit}
                        toggleStatus={toggleStatus}
                        handleDeleteTenant={handleDeleteTenant}
                        setViewingUsers={setViewingUsers}
                    />
                ) : (
                    <PendingGrid
                        pendingRegistrations={pendingRegistrations}
                        handleActivateRegistration={handleActivateRegistration}
                        handleDeleteRegistration={handleDeleteRegistration}
                        saving={saving}
                        BILLING_CYCLES={BILLING_CYCLES}
                    />
                )}
            </main>

            {/* Modais */}
            {isAdding && (
                <ModalAdicionarUnidade
                    newId={newId} setNewId={setNewId}
                    newName={newName} setNewName={setNewName}
                    newOwnerPhone={newOwnerPhone} setNewOwnerPhone={setNewOwnerPhone}
                    newAdmins={newAdmins} setNewAdmins={setNewAdmins}
                    newPlan={newPlan} setNewPlan={setNewPlan}
                    newCycle={newCycle} setNewCycle={setNewCycle}
                    SUBSCRIPTION_PLANS={SUBSCRIPTION_PLANS}
                    BILLING_CYCLES={BILLING_CYCLES}
                    setIsAdding={setIsAdding}
                    handleAddTenant={handleAddTenant}
                    saving={saving}
                />
            )}

            {isEditing && (
                <ModalEditarUnidade
                    editingTenant={editingTenant}
                    editOwnerPhone={editOwnerPhone} setEditOwnerPhone={setEditOwnerPhone}
                    editAdmins={editAdmins} setEditAdmins={setEditAdmins}
                    editPlan={editPlan} setEditPlan={handleEditPlanChange}
                    editCycle={editCycle} setEditCycle={handleEditCycleChange}
                    editDueDate={editDueDate} setEditDueDate={setEditDueDate}
                    SUBSCRIPTION_PLANS={SUBSCRIPTION_PLANS}
                    BILLING_CYCLES={BILLING_CYCLES}
                    setIsEditing={setIsEditing}
                    setEditingTenant={setEditingTenant}
                    handleSaveTenantDetails={handleSaveTenantDetails}
                />
            )}

            {viewingUsers && (
                <ModalUsuarios
                    viewingUsers={viewingUsers}
                    setViewingUsers={setViewingUsers}
                    handleDeleteUser={handleDeleteUser}
                />
            )}

            {isBroadcasting && (
                <ModalComunicado
                    broadcastData={broadcastData}
                    setBroadcastData={setBroadcastData}
                    setIsBroadcasting={setIsBroadcasting}
                    handleClearBroadcast={handleClearBroadcast}
                    handleUpdateBroadcast={handleUpdateBroadcast}
                    saving={saving}
                />
            )}

            <footer className="master-footer">
                <div>StartWebb Platform v2.0</div>
                <div>Logado como: <strong>{userEmail}</strong></div>
            </footer>
        </div>
    );
};

export default PainelMaster;
